import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Fingerprint } from 'lucide-react';
import { RolesTable, RolesFilters, RoleFormModal, CreateRoleModal } from '../components';
import { useRoles, useRolesCount, useCreateRole, useUpdateRole, useDeleteRole } from '../hooks/useRoles';
import { roleKeys } from '../hooks/queryKeys';
import { exportRolesToCSV, copyRolesToClipboard } from '../utils/csvExport';
import { exportRolesToExcel } from '../utils/excelExport';
import type { Role, RolesFilters as RolesFiltersType, CreateRoleInput, UpdateRoleInput, RoleColumnKey } from '../types';
import { DEFAULT_VISIBLE_COLUMNS } from '../types';

// =============================================================================
// Filter Persistence
// =============================================================================

const FILTERS_STORAGE_KEY = 'atek_roles_filters';
const COLUMNS_STORAGE_KEY = 'atek_roles_columns';

interface PersistedFilters {
  search: string;
  pageSize: number;
  sortKey: RolesFiltersType['sortKey'];
  sortDirection: 1 | -1;
}

function loadPersistedFilters(): PersistedFilters {
  try {
    const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return {
        search: parsed.search ?? '',
        pageSize: parsed.pageSize ?? 25,
        sortKey: parsed.sortKey ?? 'roleId',
        sortDirection: parsed.sortDirection ?? 1,
      };
    }
  } catch {
    // Ignore parse errors
  }
  return { search: '', pageSize: 25, sortKey: 'roleId', sortDirection: 1 };
}

function savePersistedFilters(filters: PersistedFilters): void {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
}

function loadVisibleColumns(): RoleColumnKey[] {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) return parsed;
    }
  } catch {
    // Ignore parse errors
  }
  return [...DEFAULT_VISIBLE_COLUMNS];
}

function saveVisibleColumns(columns: RoleColumnKey[]): void {
  try {
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  } catch {
    // Ignore storage errors
  }
}

// =============================================================================
// RolesPage
// =============================================================================

export default function RolesPage() {
  const { t } = useTranslation('roles');
  const queryClient = useQueryClient();

  // Load persisted state
  const persisted = loadPersistedFilters();

  // Filter state
  const [search, setSearch] = useState(persisted.search);
  const [debouncedSearch, setDebouncedSearch] = useState(persisted.search);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(persisted.pageSize);
  const [sortKey, setSortKey] = useState<RolesFiltersType['sortKey']>(persisted.sortKey);
  const [sortDirection, setSortDirection] = useState<1 | -1>(persisted.sortDirection);

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<RoleColumnKey[]>(loadVisibleColumns);

  // Modal state
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Persist filters (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      savePersistedFilters({ search, pageSize, sortKey, sortDirection });
    }, 500);
    return () => clearTimeout(timer);
  }, [search, pageSize, sortKey, sortDirection]);

  // Build filters
  const filters: RolesFiltersType = {
    search: debouncedSearch || undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
  };

  // Queries and mutations
  const rolesQuery = useRoles(filters);
  const countQuery = useRolesCount({ search: debouncedSearch || undefined });
  const createRoleMutation = useCreateRole();
  const updateRoleMutation = useUpdateRole();
  const deleteRoleMutation = useDeleteRole();

  // Use accurate count when available, fall back to estimated total
  const total = countQuery.data ?? rolesQuery.data?.total ?? 0;

  // Handlers
  const handleSort = (key: RolesFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection((prev) => (prev === 1 ? -1 : 1));
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(0);
  };

  const handleCreateRole = async (data: CreateRoleInput) => {
    await createRoleMutation.mutateAsync(data);
    setShowCreateModal(false);
  };

  const handleUpdateRole = async (data: UpdateRoleInput) => {
    if (!selectedRole) return;
    await updateRoleMutation.mutateAsync({ roleId: selectedRole._id, data });
    setSelectedRole(null);
  };

  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    await deleteRoleMutation.mutateAsync(selectedRole._id);
    setSelectedRole(null);
  };

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: roleKeys.all });
  }, [queryClient]);

  const handleVisibleColumnsChange = useCallback((columns: RoleColumnKey[]) => {
    setVisibleColumns(columns);
    saveVisibleColumns(columns);
  }, []);

  // Export handlers
  const roles = rolesQuery.data?.roles || [];

  const handleExportCSV = useCallback(() => {
    try {
      exportRolesToCSV(roles, 'roles');
      toast.success(t('toolbar.exportSuccess', { count: roles.length }));
    } catch {
      toast.error(t('toolbar.exportError'));
    }
  }, [roles, t]);

  const handleExportExcel = useCallback(() => {
    try {
      exportRolesToExcel(roles);
      toast.success(t('toolbar.exportSuccess', { count: roles.length }));
    } catch {
      toast.error(t('toolbar.exportError'));
    }
  }, [roles, t]);

  const handleCopyToClipboard = useCallback(async () => {
    try {
      await copyRolesToClipboard(roles);
      toast.success(t('toolbar.copySuccess', { count: roles.length }));
    } catch {
      toast.error(t('toolbar.exportError'));
    }
  }, [roles, t]);

  const isLoading = rolesQuery.isLoading;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Fingerprint className="h-6 w-6 text-gray-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('page.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('page.subtitle')}
            </p>
          </div>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('actions.create')}
        </Button>
      </div>

      {/* Filters */}
      <RolesFilters
        search={search}
        onSearchChange={setSearch}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        onRefresh={handleRefresh}
        isFetching={rolesQuery.isFetching}
        onExportCSV={handleExportCSV}
        onExportExcel={handleExportExcel}
        onCopyToClipboard={handleCopyToClipboard}
        visibleColumns={visibleColumns}
        onVisibleColumnsChange={handleVisibleColumnsChange}
      />

      {/* Table */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: Math.min(pageSize, 10) }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : rolesQuery.error ? (
        <div className="border rounded-lg p-8 text-center">
          <p className="text-red-600">{t('toast.loadError', 'Failed to load roles')}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {rolesQuery.error instanceof Error
              ? rolesQuery.error.message
              : t('toast.unexpectedError', 'An unexpected error occurred')}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => rolesQuery.refetch()}
          >
            {t('common.tryAgain', 'Try Again')}
          </Button>
        </div>
      ) : (
        <RolesTable
          roles={roles}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onRowClick={setSelectedRole}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          visibleColumns={visibleColumns}
          canModify={true}
        />
      )}

      {/* Edit Modal */}
      {selectedRole && (
        <RoleFormModal
          open={!!selectedRole}
          onOpenChange={(open) => !open && setSelectedRole(null)}
          role={selectedRole}
          onSave={handleUpdateRole}
          onDelete={handleDeleteRole}
          isSubmitting={updateRoleMutation.isPending || deleteRoleMutation.isPending}
        />
      )}

      {/* Create Modal */}
      <CreateRoleModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSave={handleCreateRole}
        isSubmitting={createRoleMutation.isPending}
      />
    </div>
  );
}
